(() => {
  if (customElements.get('rr-campaign-video')) return;

  class RRCampaignVideo extends HTMLElement {
    connectedCallback() {
      if (this.controller || !this.querySelector('[data-video-desktop]')) return;
      this.poster = this.querySelector('[data-poster]');
      this.status = this.querySelector('[data-status]');
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.mobileQuery = matchMedia('(max-width: 749px)');
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      this.inView = !('IntersectionObserver' in window);
      this.blocked = false;
      this.selectPlayer();
      this.mobileQuery.addEventListener('change', () => this.selectPlayer(), options);
      this.motionQuery.addEventListener('change', () => this.syncPlayback(), options);
      this.connection?.addEventListener('change', () => this.syncPlayback(), options);
      document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries[0]?.isIntersecting === true;
          this.syncPlayback();
        }, { threshold: 0.01 });
        this.observer.observe(this);
      }
    }

    selectPlayer() {
      const template = (this.mobileQuery.matches && this.querySelector('[data-video-mobile]'))
        || this.querySelector('[data-video-desktop]');
      if (!template || (template === this.activeTemplate && this.activeMobile === this.mobileQuery.matches)) return;
      this.playRequest = (this.playRequest || 0) + 1;
      this.playerController?.abort();
      this.releasePlayer();
      this.activeTemplate = template;
      this.activeMobile = this.mobileQuery.matches;
      this.pending = false;
      this.blocked = false;
      if (this.status) this.status.textContent = '';
      if (this.poster) this.poster.hidden = false;
      this.classList.remove('is-playing');
      this.syncPlayback();
    }

    mountPlayer() {
      if (this.player || !this.activeTemplate) return;
      const original = this.activeTemplate.content.querySelector('video');
      if (!original) return;
      this.player = original.cloneNode(true);
      // Select one Shopify MP4 rendition proportionate to the displayed size.
      const sources = [...this.player.querySelectorAll('source')];
      const mp4 = sources.filter(source => source.type === 'video/mp4' && Number(source.dataset.width) > 0)
        .sort((a, b) => Number(a.dataset.width) - Number(b.dataset.width));
      const displayedWidth = this.getBoundingClientRect().width || (this.mobileQuery.matches ? 360 : 1280);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const target = Math.max(360, Math.min(1920, Math.ceil(displayedWidth * dpr)));
      const chosen = mp4.find(source => Number(source.dataset.width) >= target) || mp4[mp4.length - 1];
      if (chosen) sources.forEach(source => { if (source !== chosen) source.remove(); });

      this.player.autoplay = true;
      this.player.controls = false;
      this.player.muted = true;
      this.player.defaultMuted = true;
      this.player.loop = true;
      this.player.playsInline = true;
      this.player.preload = 'none';
      this.player.hidden = true;
      this.insertBefore(this.player, this.status || null);
      this.pending = false;
      this.playerController = new AbortController();
      const options = { signal: this.playerController.signal };
      this.player.addEventListener('playing', () => {
        this.player.hidden = false;
        this.classList.add('is-playing');
        if (this.poster) this.poster.hidden = true;
      }, options);
      this.player.addEventListener('pause', () => this.classList.remove('is-playing'), options);
      this.player.addEventListener('error', () => this.fail(), options);
    }

    syncPlayback() {
      // Autoplay without controls: never force motion on a user who opts out.
      if (!this.inView || document.hidden || this.motionQuery.matches || this.connection?.saveData) {
        this.stop();
      } else if (!this.blocked) {
        this.mountPlayer();
        this.play();
      }
    }

    async play() {
      if (!this.player || this.pending || !this.player.paused) return;
      const request = ++this.playRequest;
      const player = this.player;
      this.pending = true;
      player.hidden = false;
      if (this.status) this.status.textContent = '';
      try {
        await player.play();
      } catch (error) {
        if (this.isConnected && request === this.playRequest && error.name !== 'AbortError') {
          if (error.name === 'NotAllowedError') {
            this.blocked = true;
            player.hidden = true;
            if (this.poster) this.poster.hidden = false;
            this.classList.remove('is-playing');
          } else {
            this.fail();
          }
        }
      } finally {
        if (request === this.playRequest) this.pending = false;
      }
    }

    stop() {
      this.playRequest = (this.playRequest || 0) + 1;
      this.pending = false;
      this.player?.pause();
      this.classList.remove('is-playing');
    }

    fail() {
      this.blocked = true;
      this.stop();
      if (this.player) this.player.hidden = true;
      if (this.poster) this.poster.hidden = false;
      if (this.status) this.status.textContent = this.dataset.error || '';
    }

    releasePlayer() {
      if (!this.player) return;
      this.player.pause();
      this.player.removeAttribute('src');
      this.player.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
      this.player.load();
      this.player.remove();
      this.player = null;
    }

    disconnectedCallback() {
      this.playRequest = (this.playRequest || 0) + 1;
      this.playerController?.abort();
      this.controller?.abort();
      this.observer?.disconnect();
      this.releasePlayer();
      this.controller = null;
      this.playerController = null;
      this.activeTemplate = null;
      this.classList.remove('is-playing');
      if (this.poster) this.poster.hidden = false;
    }
  }

  customElements.define('rr-campaign-video', RRCampaignVideo);
})();
